import { Box, Typography, useMediaQuery } from '@mui/material';
import { BarChart, Bar, YAxis, ResponsiveContainer, ReferenceLine, CartesianGrid, XAxis } from 'recharts';
import { ABOVE_RANGE, BELOW_RANGE, CONCENTRATION_UNITS, INCONCLUSIVE, NOT_DETECTED } from '../../constants/report';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getDisplayLabel, truncateToTwoDecimals } from '../../utils/reports';
import { BadgeLabelConcentration } from './elements/BadgeLabelConcentration';
import { CompoundChartChipMeaning } from '../../reports/tabsContent/CompoundChartChipMeaning';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';
import CompoundLabelChip from '../../reports/details/CompoundLabelChip';

type CompoundDetailsValues = {
  nameAbbr: string;
  value: number;
  testNameOnly: string;
  lowerReportableValue: number;
  higherReportableValue: number;
  detectionFreq: string;
  label: string;
  color: string;
  fillFontColor: string;
  detectionStatus: string;
};

type CompoundDetailsChartProps = {
  compoundDetails: CompoundDetailsValues;
};

const CompoundDetailsChart: React.FC<CompoundDetailsChartProps> = ({ compoundDetails }) => {
  const {
    value,
    nameAbbr,
    testNameOnly,
    lowerReportableValue,
    higherReportableValue,
    detectionFreq,
    label,
    color,
    fillFontColor,
    detectionStatus,
  } = compoundDetails;
  const { theme } = useThemeContext();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('md'));
  let BAR_WIDTH_SIZE = isSmallDevice ? 90 : 50;
  const [barPosition, setBarPosition] = useState({ x: 0, y: 0, width: 0 });
  const labelRef = useRef<{ x: number; y: number; width: number } | null>(null);
  const [initialBarPosition, setInitialBarPosition] = useState(false);

  // For these labels, the value and the bar graph will not be shown
  const isExcludedLabel = (label: string) => label === BELOW_RANGE || label === INCONCLUSIVE;
  const bodyFontSize = '1rem';

  // Using useRef to store and avoid the warning from react about
  // Warning: Cannot update a component (CompoundDetailsChart) while rendering a different component
  // because the direct use of setBarPosition in handleLabelRender
  const handleLabelRender = ({ x, y, width }: { x: number; y: number; width: number }) => {
    labelRef.current = { x, y, width };
    if (!initialBarPosition) {
      setInitialBarPosition(true);
    }
    return <></>;
  };

  // This function is used to calculate the top range of the YAxis
  // let's revise it in the future as we are getting a ton of results
  // with values less than 1
  const calculateTopRange = (value: number, higherTopValue: number) => {
    if (value < 0.5) return 0.5;
    if (value < 1) return 1;
    if (value >= 1 && value < 5) return 5;
    if (value >= 5 && value < 10) return 10;
    if (value >= 10 && value < 20) return 20;
    if (value >= 20 && value < 30) return 30;
    if (value >= 30 && value < 40) return 40;

    return higherTopValue;
  };

  const displayedValue = (): number | string => {
    if (label === ABOVE_RANGE) return '---';

    return truncateToTwoDecimals(value);
  };

  const topRange = calculateTopRange(value, higherReportableValue);
  const HEIGHT_FOR_DETECTED = 30;
  const HEIGHT_FOR_OUT_OF_RANGE = 10;
  const HEIGHT_FOR_LOW_RANGE = 10;

  const calculateRelativeConcentration = useCallback(
    (concentrationValue: number) => {
      let relativeConcentrationValue;

      if (concentrationValue <= lowerReportableValue) {
        relativeConcentrationValue = concentrationValue * 10;
      }

      if (concentrationValue > lowerReportableValue && concentrationValue <= higherReportableValue) {
        relativeConcentrationValue = (concentrationValue * HEIGHT_FOR_DETECTED) / topRange + 10;

        // For low values we set a default value to be shown in the right stacked bar chart
        if (relativeConcentrationValue < 10) {
          relativeConcentrationValue += 10.5;
        }
      }

      if (concentrationValue > higherReportableValue) {
        relativeConcentrationValue = 45;
      }
      return relativeConcentrationValue;
    },
    [lowerReportableValue, topRange],
  );

  useEffect(() => {
    if (
      labelRef.current &&
      (barPosition.x !== labelRef.current.x ||
        barPosition.y !== labelRef.current.y ||
        barPosition.width !== labelRef.current.width)
    ) {
      setBarPosition({
        x: labelRef.current.x,
        y: labelRef.current.y,
        width: labelRef.current.width,
      });
    }
  }, [labelRef.current, initialBarPosition]);

  const renderLevelValue = () => {
    const decoratedValue = displayedValue();
    if (isNaN(decoratedValue as any) || isExcludedLabel(label) || decoratedValue === 0)
      return (
        <Typography
          component="div"
          variant="body2"
          sx={{
            color:
              theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderLevelValue.nonDisplayValueColor,
            fontSize: '2rem',
            width: { xs: '100%', md: '57px' },
            textAlign: { xs: 'center', md: 'left' },
            letterSpacing: '-4px',
            height: '53px',
          }}
        >
          ---
        </Typography>
      );

    return (
      <Box sx={{ display: 'flex', width: { xs: 'inherit', md: '100%' } }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: '2.7rem',
            fontWeight: '500',
            color:
              theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderLevelValue.displayValueColor,
          }}
        >
          {displayedValue()}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            marginLeft: '5px',
            marginTop: '4px',
            color: theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderLevelValue.levelColor,
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
            Level
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: bodyFontSize,
              marginTop: '-7px',
              color: theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderLevelValue.unitColor,
            }}
          >
            {CONCENTRATION_UNITS}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderBarChart = () => {
    if (isExcludedLabel(label)) return;

    const chartData = [
      {
        name: 'A',
        x: HEIGHT_FOR_LOW_RANGE,
        y: HEIGHT_FOR_DETECTED,
        z: HEIGHT_FOR_OUT_OF_RANGE,
        originalConcentration: displayedValue(),
        relativeConcentration: calculateRelativeConcentration(value),
      },
    ];

    const referenceLines = [
      { y: 10, label: lowerReportableValue },
      { y: 40, label: topRange },
    ];
    return (
      <Box sx={{ marginTop: { xs: '-23px', md: '0px' } }}>
        <ResponsiveContainer width="100%" height={300} minWidth={200}>
          <BarChart data={chartData}>
            <CartesianGrid vertical={false} horizontal={false} />
            <XAxis
              label={{
                value: nameAbbr,
                style: {
                  fill: theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart
                    .xAxisFillColor,
                  fontSize: '15px',
                  textAnchor: 'middle',
                },
              }}
              axisLine={false}
              tickLine={false}
              tick={false}
            />
            <YAxis
              label={{
                value: 'ng/mL',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fill: theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart
                    .yAxisFillColor,
                  fontSize: '15px',
                },
              }}
              axisLine={false}
              tickLine={false}
              tick={false}
            />

            {referenceLines.map((line, index) => (
              <ReferenceLine
                key={index}
                y={line.y}
                stroke={
                  theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart
                    .referenceLines.strokeColor
                }
                strokeWidth={2}
                label={{
                  value: line.label,
                  position: 'left',
                  fill: theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart
                    .referenceLines.labelFillColor,
                  fontSize: '15px',
                }}
                ifOverflow="visible"
              />
            ))}

            <Bar
              dataKey="x"
              stackId="a"
              fill={
                theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart.xData
                  .fillColor
              }
              barSize={BAR_WIDTH_SIZE}
              label={handleLabelRender}
            />
            <Bar
              dataKey="y"
              stackId="a"
              fill={
                theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart.yData
                  .fillColor
              }
              barSize={BAR_WIDTH_SIZE}
            />
            <Bar
              dataKey="z"
              stackId="a"
              fill={
                label.toLowerCase().trim().replace(/\s+/g, ' ').includes(ABOVE_RANGE.toLowerCase()) ||
                label.toLowerCase().trim().replace(/\s+/g, ' ').includes(NOT_DETECTED.toLowerCase())
                  ? theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart.zData
                      .specialLabelFillColor
                  : theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart.zData
                      .fillColor
              }
              barSize={BAR_WIDTH_SIZE}
              radius={[10, 10, 0, 0]}
            />

            {label !== ABOVE_RANGE && (
              <ReferenceLine
                y={chartData[0].relativeConcentration}
                stroke={
                  theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart
                    .referenceLines.defaultStrokeColor
                }
                strokeWidth={3}
                strokeDasharray="4 4"
                ifOverflow="visible"
                label={(props) => (
                  <BadgeLabelConcentration
                    labelColor={theme.custom.report.dataDecoration[detectionStatus].backgroundColor}
                    positionY={props.viewBox.y}
                    barX={barPosition.x}
                    barWidth={barPosition.width}
                    value={chartData[0].originalConcentration}
                  />
                )}
              />
            )}

            <ReferenceLine
              y={0}
              stroke={
                theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart
                  .referenceLines.y0.strokeColor
              }
              label={{
                value: 0.0,
                position: 'left',
                fill: theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderBarChart.barChart
                  .referenceLines.y0.labelFillColor,
                fontSize: '15px',
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderCompoundResultsLevel = () => {
    if (label === ABOVE_RANGE) return <></>;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexDirection: { xs: 'row', md: 'column' },
          '@media (max-width: 444px)': {
            flexDirection: 'column',
            gap: '10px',
          },
        }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{ mb: '6px', marginTop: '8px', fontSize: '12px', textAlign: 'left' }}
        >
          Your result for {testNameOnly} is:
        </Typography>

        {renderLevelValue()}
      </Box>
    );
  };

  const renderCardWithLabelChip = () => {
    const displayLabel = getDisplayLabel(label)
    return (
      <Box
        sx={{
          display: 'flex',
          bgcolor:
            theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderCardWithLabelChip.backgroundColor,
          color: theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderCardWithLabelChip.color,
          p: '12px 12px',
          textAlign: { xs: 'center', md: 'left' },
          flexDirection: 'column',
          borderRadius: '14px',
          border: `2px solid ${theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderCardWithLabelChip.borderColor}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '20px',
          }}
        >
          <Box sx={{ flex: 0 }}>
            <CompoundLabelChip
              label={displayLabel}
              backgroundColor={theme.custom.report.dataDecoration[detectionStatus].backgroundColor}
              fontColor={theme.custom.report.dataDecoration[detectionStatus].fillFontColor}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '5px',
              marginTop: { xs: '2px', md: '-3px' },
              flexDirection: { md: 'row' },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                color:
                  theme.custom.report.details.compoundCardsList.compoundDetailsChart.renderCardWithLabelChip
                    .detectionFrequencyColor,
              }}
            >
              {detectionFreq} detection frequency
            </Typography>
          </Box>
        </Box>

        {renderCompoundResultsLevel()}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, padding: { xs: '0', md: '0' } }}>
      <Box sx={{ width: '100%' }}>
        {renderCardWithLabelChip()}
        {renderBarChart()}
        {isSmallDevice && (
          <CompoundChartChipMeaning
            lowerReportableValue={lowerReportableValue}
            higherReportableValue={higherReportableValue}
          />
        )}
      </Box>
    </Box>
  );
};

export default CompoundDetailsChart;
